import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Navbar } from './components/Navbar';
import { LoginPage }            from './pages/LoginPage';
import { RegisterPage }         from './pages/RegisterPage';
import { DashboardPage }        from './pages/DashboardPage';
import { CoursesPage }          from './pages/CoursesPage';
import { StudyBuddyPage }       from './pages/StudyBuddyPage';
import { TeacherDashboardPage } from './pages/TeacherDashboardPage';
import { CourseDetailPage }     from './pages/CourseDetailPage';
import { LessonPage }           from './pages/LessonPage';
import { QuizPage }             from './pages/QuizPage';
import { LessonEditorPage }     from './pages/LessonEditorPage';
import { useEffect }            from 'react';
import toast                    from 'react-hot-toast';
import { FlashcardsPage } from './pages/FlashcardsPage';


function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />
      <main>{children}</main>
    </div>
  );
}

// Redirects students away from teacher-only pages
function TeacherRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center
                      bg-gray-950">
        <div className="text-green-400">Loading...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (user.role === 'STUDENT') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  useEffect(() => {
    const handleExpiry = () => {
      toast.error('Session expired — please login again', {
        duration: 2000,
        icon: '🔒',
      });
    };
    window.addEventListener('auth:expired', handleExpiry);
    return () => window.removeEventListener('auth:expired', handleExpiry);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#1F2937',
              color: '#E2E8F0',
              border: '1px solid #374151',
            },
          }}
        />
        <Routes>
          {/* Public */}
          <Route path="/login"    element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Protected — all logged in users */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout><DashboardPage /></Layout>
            </ProtectedRoute>
          } /> 
      
           {/* Catch-all — MUST be last */}
<Route path="/flashcards" element={
  <ProtectedRoute>
    <Layout><FlashcardsPage /></Layout>
  </ProtectedRoute>
} />
          

          <Route path="/courses" element={
            <ProtectedRoute>
              <Layout><CoursesPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/courses/:courseId" element={
            <ProtectedRoute>
              <Layout><CourseDetailPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/courses/:courseId/lessons/:lessonId" element={
            <ProtectedRoute>
              <Layout><LessonPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/courses/:courseId/quiz/:assessmentId" element={
            <ProtectedRoute>
              <Layout><QuizPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/study-buddy" element={
            <ProtectedRoute>
              <Layout><StudyBuddyPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/lessons/:lessonId/edit" element={
            <ProtectedRoute>
              <Layout><LessonEditorPage /></Layout>
            </ProtectedRoute>
          } />

          {/* Teacher only — students redirected to dashboard */}
          <Route path="/teacher" element={
            <TeacherRoute>
              <Layout><TeacherDashboardPage /></Layout>
            </TeacherRoute>
          } />

          {/* Catch-all — MUST be last */}
          <Route path="/"  element={<Navigate to="/dashboard" replace />} />
          <Route path="*"  element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}